// ── Desbloqueo de autoplay (2026-08-18) ──────────────────────────────────────
// Chrome (y Safari) bloquean audio.play() que no venga de un gesto del usuario:
// "The play method is not allowed by the user agent... because the user denied
// permission". En el onboarding la respuesta hablada de Evo Brain se dispara
// sola al llegar el mensaje, sin clic de por medio, así que el navegador la
// rechaza siempre.
//
// La regla del navegador NO es "cada reproducción necesita un gesto": es que el
// documento necesita HABER TENIDO uno. Reproducir un audio mudo dentro del
// primer clic/tecla real deja el permiso concedido para el resto de la sesión,
// y a partir de ahí un .play() programático pasa.
//
// Se engancha una sola vez, a nivel de documento y en captura, para no depender
// de que el clic llegue a ningún componente en particular — el primer gesto
// sirve, sea donde sea.

let unlocked = false;
let listening = false;
const waiters: Array<() => void> = [];

/** true si el documento ya tuvo un gesto y el audio programático va a pasar. */
export function isAudioUnlocked(): boolean {
  return unlocked;
}

function markUnlocked(): void {
  if (unlocked) return;
  unlocked = true;
  removeListeners();
  // Se despierta a quien haya quedado esperando: la respuesta hablada que el
  // navegador rechazó puede reintentarse ahora sin que el usuario repita nada.
  const pending = waiters.splice(0, waiters.length);
  for (const w of pending) { try { w(); } catch { /* un waiter roto no frena a los demás */ } }
}

const GESTURES = ['pointerdown', 'keydown', 'touchend'] as const;

/**
 * WAV mudo de 1 muestra, como Blob.
 *
 * blob: y NO data: — el CSP de la app declara `media-src 'self' blob:`, así que
 * un data: URI queda bloqueado y el navegador escupe un console.error. Lo
 * detectó el smoke check del deploy (que falla ante cualquier error de consola)
 * y disparó el rollback automático antes de que llegara a producción. blob: ya
 * está permitido: no hace falta aflojar el CSP para esto.
 */
function silentWavUrl(): string {
  const bytes = new Uint8Array([
    0x52, 0x49, 0x46, 0x46, 0x24, 0x00, 0x00, 0x00, 0x57, 0x41, 0x56, 0x45,
    0x66, 0x6d, 0x74, 0x20, 0x10, 0x00, 0x00, 0x00, 0x01, 0x00, 0x01, 0x00,
    0x80, 0x3e, 0x00, 0x00, 0x00, 0x7d, 0x00, 0x00, 0x02, 0x00, 0x10, 0x00,
    0x64, 0x61, 0x74, 0x61, 0x00, 0x00, 0x00, 0x00,
  ]);
  return URL.createObjectURL(new Blob([bytes], { type: 'audio/wav' }));
}

function onGesture(): void {
  // Reproducir algo mudo DENTRO del gesto hace que el navegador cuente la
  // reproducción como originada en él y le conceda el permiso al documento.
  // Se desbloquea HTMLAudioElement, que es lo que usa el TTS (no un
  // AudioContext).
  let url: string | null = null;
  try {
    url = silentWavUrl();
    const a = new Audio(url);
    a.volume = 0;
    const done = () => { if (url) URL.revokeObjectURL(url); markUnlocked(); };
    void a.play().then(() => { a.pause(); done(); }).catch(() => {
      // Aunque el mudo falle, el documento YA tuvo un gesto — que es la
      // condición que de verdad importa. Marcar igual evita quedar trabado
      // esperando un desbloqueo que nunca va a confirmarse.
      done();
    });
  } catch {
    if (url) URL.revokeObjectURL(url);
    markUnlocked();
  }
}

function removeListeners(): void {
  if (!listening) return;
  for (const g of GESTURES) document.removeEventListener(g, onGesture, true);
  listening = false;
}

/** Idempotente — se puede llamar desde varios lugares sin duplicar listeners. */
export function installAudioUnlock(): void {
  if (unlocked || listening || typeof document === 'undefined') return;
  listening = true;
  for (const g of GESTURES) document.addEventListener(g, onGesture, true);
}

/** Corre `fn` cuando el audio esté desbloqueado. Si ya lo está, ahora mismo. */
export function whenAudioUnlocked(fn: () => void): void {
  if (unlocked) { fn(); return; }
  installAudioUnlock();
  waiters.push(fn);
}

/** ¿Este error es el bloqueo de autoplay y no una falla real de audio? */
export function isAutoplayBlocked(err: unknown): boolean {
  if (err instanceof DOMException && err.name === 'NotAllowedError') return true;
  const msg = err instanceof Error ? err.message : String(err);
  return /not allowed by the user agent|denied permission|NotAllowedError|autoplay/i.test(msg);
}
